import type {
    BlinkMetadataRequest,
    BlinkMetadataResponse,
} from "@amzn/ads-blink-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { cloneDeep } from "lodash";

import { getCustomerId } from "../../api/adsApi/adsApi.config";
import { getBlinkApiClientV2 } from "../../api/blinkApi/blinkApi";
import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import type {
    BaseFilterOptionModel,
    UnifiedFilters,
} from "../../types/filters/common";
import type { AllUnifiedFilterKeys } from "../../types/filters/unified";
import { UnifiedFilterKeys } from "../../types/filters/unified";
import { InsightFamilyName } from "../../types/providers";
import {
    getCurrentProviders,
    getUnifiedSearchQueryExpressionV2,
} from "../../utils/unifiedContent.utils";

export interface UnifiedContentFiltersActionProps {
    advertiserId?: string;
    marketplaceId?: string;
}

export interface UnifiedContentFiltersExecuteProps {
    ignoreField?: AllUnifiedFilterKeys;
    ignoreScope?: boolean;
}

export interface UnifiedContentFiltersActionResponse {
    filters: Partial<UnifiedFilters>;
    scopes: string[];
}

const emptyResponse: UnifiedContentFiltersActionResponse = {
    filters: {},
    scopes: [],
};

export function useGetUnifiedContentsFiltersV2Async({
    advertiserId,
    marketplaceId,
}: UnifiedContentFiltersActionProps): [
    AsyncState<UnifiedContentFiltersActionResponse>,
    UseAsyncActions<
        UnifiedContentFiltersActionResponse,
        [UnifiedContentFiltersExecuteProps]
    >,
] {
    const [insightsState, insightsAction] = useAsync(
        async (props: UnifiedContentFiltersExecuteProps) => {
            const { ignoreField, ignoreScope } = props;
            const request: BlinkMetadataRequest = {
                advertiserId,
                marketplaceId,
                clientId: window.location.host,
                queryExpression: getUnifiedSearchQueryExpressionV2(
                    ignoreField,
                    ignoreScope,
                ),
                targetProviders: getCurrentProviders(ignoreScope),
                userAlias: authStore.getState().username,
                customerId: getCustomerId(),
            };

            const metadataResponse = await (
                await getBlinkApiClientV2()
            ).cerebroMetadata(request);

            return generateFilterFromMetadata(metadataResponse);
        },
        emptyResponse,
    );

    return [insightsState, insightsAction];
}

const generateFilterFromMetadata = ({
    metadata,
}: BlinkMetadataResponse): UnifiedContentFiltersActionResponse => {
    const filters = cloneDeep(emptyResponse.filters);
    const isAiInsightsV2Enabled = FeatureFlagsStore.getState().aiInsightsV2;

    metadata
        ?.filter((m) => m.key && m.values)
        .filter((m) =>
            Object.values(UnifiedFilterKeys).includes(
                m.key! as UnifiedFilterKeys,
            ),
        )
        .map((m) => {
            filters[m.key!] =
                m.values?.map((val) => {
                    const transformedId =
                        isAiInsightsV2Enabled &&
                        val.id?.includes(InsightFamilyName.INSIGHT_GENIE)
                            ? InsightFamilyName.INSIGHT_GENIE
                            : val.id;
                    const transformedName =
                        isAiInsightsV2Enabled &&
                        val.name?.includes(InsightFamilyName.INSIGHT_GENIE)
                            ? InsightFamilyName.INSIGHT_GENIE
                            : val.name;

                    return {
                        id: transformedId,
                        label: transformedName,
                        isSelected: false,
                    } as BaseFilterOptionModel;
                }) ?? [];
        });

    const scopes =
        metadata
            ?.filter((m) => m.key === "UNIFIED_TYPE")[0]
            ?.values?.filter((val) => val.id)
            ?.map((val) => {
                const transformedId =
                    isAiInsightsV2Enabled &&
                    val.id?.includes(InsightFamilyName.INSIGHT_GENIE)
                        ? InsightFamilyName.INSIGHT_GENIE
                        : val.id;
                return transformedId!;
            }) ?? [];

    return {
        filters,
        scopes,
    };
};
